import React, { useEffect, useState } from 'react'

interface IPreloadImagesProps {
  images: string[]
  handleLoading?: (count: number) => void
}

export const PreloadImages: React.FC<IPreloadImagesProps> = (props: IPreloadImagesProps) => {
  const { images, handleLoading } = props

  const [count, setCount] = useState(1)

  const loaded = () => {
    setCount((value) => ++value)
  }

  useEffect(() => {
    handleLoading && handleLoading(count)
  }, [count, handleLoading])

  return (
    <>
      {images.slice(0, count).map((item) => (
        <img
          className="d-none"
          key={`preload-img-${item}`}
          src={item}
          alt="image-preloaded"
          onLoad={loaded}
          onError={loaded}
        />
      ))}
    </>
  )
}

// Max number of in-flight preload requests at once. Lets the browser
// keep enough capacity for visible thumbnails, fonts, and analytics.
const PRELOAD_CONCURRENCY = 4

interface ImageCachePreloaderProps {
  imageUrls: string[]
  /**
   * Pass true on screens where the images are needed immediately. Defaults to
   * false so callers that warm the cache for a downstream screen don't block
   * the current screen's LCP or compete with foreground requests.
   */
  eager?: boolean
}

export const ImageCachePreloader = ({ imageUrls, eager = false }: ImageCachePreloaderProps) => {
  useEffect(() => {
    if (!imageUrls?.length) return

    const imageObjects: HTMLImageElement[] = []
    let cancelled = false
    let cursor = 0

    // Each worker repeatedly pulls the next URL and loads it. start() seeds
    // PRELOAD_CONCURRENCY workers, so we never have more than that many
    // in-flight requests at once — instead of firing all 100+ at once.
    const pump = () => {
      if (cancelled || cursor >= imageUrls.length) return
      const url = imageUrls[cursor++]
      if (!url) {
        pump()
        return
      }
      const img = new Image()
      imageObjects.push(img)
      const next = () => {
        if (cancelled) return
        img.onload = null
        img.onerror = null
        pump()
      }
      img.onload = next
      img.onerror = next
      img.src = url
    }

    const start = () => {
      if (cancelled) return
      const slots = Math.min(PRELOAD_CONCURRENCY, imageUrls.length)
      for (let i = 0; i < slots; i++) pump()
    }

    // Wait until the browser is idle before warming the cache, so this work
    // never competes with the current screen's LCP / interactivity. Fall back
    // to a short timeout for browsers without requestIdleCallback (Safari).
    let idleHandle: number | undefined
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined
    const ric = (window as any).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout: number }) => number)
      | undefined
    const cic = (window as any).cancelIdleCallback as ((h: number) => void) | undefined

    if (eager) {
      start()
    } else if (ric) {
      idleHandle = ric(start, { timeout: 2000 })
    } else {
      timeoutHandle = setTimeout(start, 1500)
    }

    return () => {
      cancelled = true
      if (idleHandle !== undefined && cic) cic(idleHandle)
      if (timeoutHandle) clearTimeout(timeoutHandle)
      imageObjects.forEach((img) => {
        img.onload = null
        img.onerror = null
      })
    }
  }, [imageUrls, eager])

  return null // This component doesn't render anything
}
